import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import TableContainer from 'Common/TableContainer';
import { MoreHorizontal, FileEdit, Trash2, CheckCircle, X, Search } from 'lucide-react';
import { Dropdown } from 'Common/Components/Dropdown';

interface UserTableProps {
  users: any[];
  roleName: string;
  rolesForUser: { [key: string]: string };
  onEdit: (user: any) => void;
  onDelete: (user: any) => void;
}

const UserTable: React.FC<UserTableProps> = ({ users, roleName, rolesForUser, onEdit, onDelete }) => {
  const columns = useMemo(() => [
    {
      header: "User Name",
      accessorKey: "userName",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          {/* <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
            {cell.row?.original?.img ? (
              <img src={cell.row.original.img} alt="" className="h-10 rounded-full" />
            ) : (
              cell?.getValue()?.split(' ')?.map((word: any) => word?.charAt(0).toUpperCase()).join('').slice(0, 2)
            )}
          </div> */}
          <div className="grow truncate">
            <h6 className="mb-1">
              <Link to="#!" className="name truncate block min-w-[200px] max-w-[200px]">{cell.getValue()?.charAt(0).toUpperCase() + cell.getValue()?.slice(1)}</Link>
            </h6>
            <p className="text-slate-500 dark:text-zink-200 truncate block min-w-[200px] max-w-[200px]">{cell.row.original.designation}</p>
          </div>
        </div>
      ),
    },
    {
      header: "First Name",
      accessorKey: "name",
      enableColumnFilter: false,
      cell: (cell: any) => (
        cell.getValue() &&
        <div className="flex items-center gap-2">
          <div className="grow truncate">
            <h6 className="mb-1">
              <Link to="#!" className="name truncate block min-w-[200px] max-w-[200px]">{cell.getValue()?.charAt(0).toUpperCase() + cell.getValue()?.slice(1)}</Link>
            </h6>
            <p className="text-slate-500 dark:text-zink-200 truncate block min-w-[200px] max-w-[200px]">{cell.row.original.designation}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Last Name",
      accessorKey: "surname",
      enableColumnFilter: false,
      cell: (cell: any) => (
        cell.getValue() &&
        <div className="flex items-center gap-2">
          <div className="grow truncate">
            <h6 className="mb-1">
              <Link to="#!" className="name truncate block min-w-[200px] max-w-[200px]">{cell.getValue()?.charAt(0).toUpperCase() + cell.getValue()?.slice(1)}</Link>
            </h6>
            <p className="text-slate-500 dark:text-zink-200 truncate block min-w-[200px] max-w-[200px]">{cell.row.original.designation}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Role",
      accessorKey: "id",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="grow">
            <h6 className="mb-1">
              <Link to="#!" className="name">{rolesForUser[cell.getValue()]}</Link>
            </h6>
          </div>
        </div>
      ),
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="grow truncate">
            <h6 className="mb-1">
              <Link to={`mailto:${cell.getValue()}`} className="name truncate block min-w-[200px] max-w-[200px]">{cell.getValue()}</Link>
            </h6>
          </div>
        </div>
      ),
    },
    {
      header: "Phone Number",
      accessorKey: "phoneNumber",
      enableColumnFilter: false,
    },
    {
      header: "Status",
      accessorKey: "isActive",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        cell.getValue() ? (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            Active
          </span>
        ) : (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-slate-100 border-transparent text-slate-500 dark:bg-slate-500/20 dark:text-zink-200 dark:border-transparent status">
            <X className="size-3 mr-1.5" />
            InActive
          </span>
        )
      ),
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => {
        if (roleName !== 'admin') return null;
        return (
          <Dropdown className="relative">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="usersAction1"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement="right-end"
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right top-[-150px] right-[100px] list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="usersAction1"
            >
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="#!"
                  onClick={() => onEdit(cell.row.original)}
                >
                  <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span>
                </Link>
              </li>
              <li>
                <Link
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                  to="#!"
                  onClick={() => onDelete(cell.row.original)}
                >
                  <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Delete</span>
                </Link>
              </li>
            </Dropdown.Content>
          </Dropdown>
        );
      },
    },
  ], [roleName, rolesForUser, onEdit, onDelete]);

  return (
    <div className="!pt-2">
      {!!users && users.length > 0 ? (
        <TableContainer
          isPagination={true}
          columns={columns}
          data={users}
          customPageSize={10}
          divclassName="h-[calc(100vh-360px)] sm:h-[calc(100vh-300px)] min-h-[200px] max-h-[calc(100vh-248px)] overflow-auto"
          tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
          theadclassName="text-left relative rounded-md bg-slate-200 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
          thclassName="px-4 py-2.5 first:pl-5 last:pr-5 font-semibold sticky top-0 bg-gray-100 z-10"
          tdclassName="cursor-pointer px-4 py-2.5 first:pl-5 last:pr-5"
          PaginationClassName="flex flex-col items-center 2xl:mt-8 xl:mt-8 mt-0 sm:mt-0 md:mt-0 md:flex-row"
          tbodyclassName=""
          trclassName="hover:bg-gray-100"
        />
      ) : (
        <div className="noresult">
          <div className="py-6 text-center">
            <Search className="size-6 mx-auto text-slate-500 fill-sky-100 dark:sky-500/20" />
            <h6 className="mt-2 mb-1">No Result Found</h6>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;